import React from 'react';
import { Baby, Cookie, Gamepad2, Lock, ShieldCheck, Users } from 'lucide-react';
import Container from './Container';

type PolicySection = {
  id: string;
  title: string;
  icon: React.ReactNode;
  summary: string;
  points: string[];
};

const policySections: PolicySection[] = [
  {
    id: 'privacy-own-games',
    title: 'Our games',
    icon: <Gamepad2 size={22} />,
    summary:
      'When you play a game developed and published by Wizantix, we do not collect personal information from you as a player.',
    points: [
      "We collect anonymous gameplay analytics — such as level completion rates, session length, and feature usage — to understand what's working and improve the experience.",
      'We also collect technical metrics like crash reports, load times, and server performance data to keep the game stable and running smoothly.',
      'None of this data identifies you personally.',
    ],
  },
  {
    id: 'privacy-children',
    title: 'Children and age-rated content',
    icon: <Baby size={22} />,
    summary:
      'Our platform accounts are for adults (18+). Wizantix does not collect personal information from game players of any age.',
    points: [
      'Wizantix platform accounts are for adults aged 18 and over only.',
      "Wizantix does not collect personal information from game players of any age, regardless of the game's target audience.",
      'Creators and publishers using the platform are solely responsible for any personal data they collect through their games, and for compliance with applicable privacy laws — including COPPA, GDPR, and other children\'s data protection regulations in their jurisdiction.',
      'Parents who believe a child has had personal data collected through a Wizantix game may contact us via the Wizantix website to request disclosure or deletion.',
    ],
  },
  {
    id: 'privacy-cookies',
    title: 'Cookies and local storage',
    icon: <Cookie size={22} />,
    summary:
      'Our games use minimal local storage to save your progress and preferences — not to track you across the web.',
    points: [
      'Game progress, settings, and preferences may be saved locally on your device so you can continue where you left off.',
      'We do not use advertising cookies or third-party tracking in our games.',
      'Analytics in our games rely only on anonymous, aggregated technical data.',
    ],
  },
  {
    id: 'privacy-security',
    title: 'Retention and security',
    icon: <Lock size={22} />,
    summary: 'We keep anonymous analytics data only as long as needed to operate and improve our games.',
    points: [
      'Anonymous gameplay analytics are retained for up to 26 months, then deleted or further anonymised.',
      'We use access controls, encryption in transit, and least-privilege practices to protect any data we hold.',
      'No online system is perfectly secure, so we cannot guarantee absolute security.',
    ],
  },
  {
    id: 'privacy-scope',
    title: 'Scope of this notice',
    icon: <ShieldCheck size={22} />,
    summary:
      'This notice covers games developed and published directly by Wizantix. It does not cover third-party games or the broader Wizantix platform.',
    points: [
      'This notice applies only to games where "Wizantix" appears as the developer in the app store listing.',
      'Third-party games published on the Wizantix platform are covered by the privacy practices of those publishers, not this notice.',
      'For the full Wizantix platform privacy policy, visit wizantix.com.',
    ],
  },
  {
    id: 'privacy-rights',
    title: 'Your rights and contact',
    icon: <Users size={22} />,
    summary:
      'Depending on where you live, you may have rights regarding data that could relate to you.',
    points: [
      'Because we do not collect personal information from players, most data-subject rights (access, deletion, portability) do not apply to gameplay analytics.',
      'If you are in the EU or UK and believe we hold personal data about you, you have rights to access, correct, delete, or restrict processing, and to lodge a complaint with your local supervisory authority.',
      'If you are in the United States, you may have rights under applicable state privacy laws depending on your location.',
      'To make any privacy request or ask a question, contact us through the Wizantix website.',
      'We may update this notice as our games evolve, and we will revise the effective date when we do.',
    ],
  },
];

const PrivacyPolicyGames: React.FC = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main>
        <section id="privacy-policy" className="py-12 px-0">
          <Container>
            <div className="max-w-2xl mx-auto">
              <div className="mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium ring-1 ring-inset ring-primary/20 mb-5">
                  <ShieldCheck size={15} />
                  Privacy Policy
                </div>
                <h1 className="text-3xl font-bold leading-tight mb-3">
                  Privacy notice for Wizantix games
                </h1>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Effective May 24, 2026. This notice explains how Wizantix handles data in the
                  games we develop and publish directly. We do not collect personal information from
                  players.
                </p>
              </div>

              <div className="space-y-5">
                {policySections.map((section) => (
                  <article
                    key={section.id}
                    id={section.id}
                    className="rounded-xl border border-border bg-card p-5 scroll-mt-6"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                        {section.icon}
                      </div>
                      <div>
                        <h2 className="text-base font-semibold">{section.title}</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">{section.summary}</p>
                      </div>
                    </div>

                    <ul className="space-y-2.5 text-sm text-muted-foreground leading-relaxed">
                      {section.points.map((point, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>

              <div className="mt-6 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground leading-relaxed">
                For the full Wizantix platform privacy policy covering hosting, developer tools, and
                AI features, visit wizantix.com.
              </div>

              <p className="mt-8 text-center text-xs text-muted-foreground">
                &copy; {new Date().getFullYear()} Wizantix. All rights reserved.
              </p>
            </div>
          </Container>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPolicyGames;
